import {
  addListToDropdown,
  Collection,
  createDropdown,
  DropdownView,
  Editor,
  ListDropdownItemDefinition,
  Plugin,
  ViewModel,
} from 'ckeditor5'
import type { PlaceholderConstant } from '@pbt/pbt-ui-components'

import { CommandName } from '../../types'

export type CommandParams = {
  name: string
  nameTranslation: string
}

type CustomDropdownView = DropdownView & {
  commandParams: CommandParams
}

function getDropdownItemsDefinitions(
  placeholderNames: PlaceholderConstant[],
): Collection<ListDropdownItemDefinition> {
  const itemDefinitions = new Collection()

  placeholderNames.forEach((group: PlaceholderConstant) => {
    const def: ListDropdownItemDefinition = {
      type: 'button',
      model: new ViewModel({
        label: group.groupTranslation,
        labelStyle: 'color: #928D8D',
        withText: true,
        isEnabled: false,
      }),
    }

    itemDefinitions.add(def)

    group.items.forEach((item) => {
      const definition: ListDropdownItemDefinition = {
        type: 'button',
        model: new ViewModel({
          commandParams: {
            name: item.name,
            nameTranslation: item.nameTranslation,
          },
          label: item.nameTranslation,
          labelStyle: 'padding: 6px 16px',
          withText: true,
        }),
      }

      itemDefinitions.add(definition)
    })
  })

  return itemDefinitions as Collection<ListDropdownItemDefinition>
}

export default class PlaceholderUI extends Plugin {
  init() {
    const editor = this.editor as Editor
    const { t } = editor

    // Can import translation the following example
    // const InsertDynamicText = enUSPbtCKEditor.Common
    //   .INSERT_DYNAMIC_TEXT as unknown as string
    // addTranslation('es', {
    //   [InsertDynamicText]: esESPbtCKEditor.Common.INSERT_DYNAMIC_TEXT,
    // })

    const placeholderNames = editor.config.get(
      'placeholderConfig.types',
    ) as PlaceholderConstant[]

    // The "placeholder" dropdown must be registered among the UI components of the editor
    // to be displayed in the toolbar.
    editor.ui.componentFactory.add('placeholder', (locale) => {
      const dropdownView = createDropdown(locale) as CustomDropdownView

      dropdownView.class = 'pbt-ckeditor-dropdown'
      dropdownView.panelPosition = 'auto'

      // Populate the list in the dropdown with items.
      addListToDropdown(
        dropdownView,
        getDropdownItemsDefinitions(placeholderNames),
      )

      dropdownView.buttonView.set({
        label: t('Insert dynamic text'),
        tooltip: true,
        withText: true,
      })

      const command = editor.commands.get(CommandName.INSERT_PLACEHOLDER)

      if (command) {
        dropdownView.bind('isEnabled').to(command)
      }

      this.listenTo(dropdownView, 'execute', (evt) => {
        const eventSource = evt.source as { commandParams: CommandParams }
        // Ensure to run the command for label groups as they're not supposed to do anything
        if (eventSource.commandParams) {
          editor.execute(CommandName.INSERT_PLACEHOLDER, {
            name: eventSource.commandParams.name,
            nameTranslation: eventSource.commandParams.nameTranslation,
          })
        }
        editor.editing.view.focus()
      })

      return dropdownView
    })
  }
}
